import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { So5Lineup } from '@sorare/gameplay/src/components/lineup/So5Lineup';
import { LineupFooter } from '@sorare/gameplay/src/layouts/LineupLayout/LineupFooter';
import { LineupRanking } from '@sorare/gameplay/src/layouts/LineupLayout/LineupRanking';
import { LineupStatus } from '@sorare/gameplay/src/layouts/LineupLayout/LineupStatus';

import type {
  NBASo5LeaderboardContenderDialogQuery,
  NBASo5LeaderboardContenderDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const SO5_LEADERBOARD_CONTENDER_DIALOG_QUERY = gql`
  query NBASo5LeaderboardContenderDialogQuery($slug: String!) {
    so5 {
      so5LeaderboardContender(slug: $slug) {
        slug
        so5Lineup {
          id
          user {
            slug
            nbaUserProfile {
              id
              clubName
            }
            ...UserName_publicUserInfoInterface
            ...Avatar_publicUserInfoInterface
            ...MyClubLink_publicUserInfoInterface
          }
        }
        ...So5Lineup_so5LeaderboardContender
        ...LineupStatus_so5LeaderboardContender
        ...LineupRanking_so5LeaderboardContender
        ...LineupFooter_so5LeaderboardContender
      }
    }
  }
  ${So5Lineup.fragments.so5LeaderboardContender}
  ${LineupStatus.fragments.so5LeaderboardContender}
  ${LineupRanking.fragments.so5LeaderboardContender}
  ${LineupFooter.fragments.so5LeaderboardContender}
  ${UserName.fragments.user}
  ${Avatar.fragments.publicUserInfoInterface}
  ${MyClubLink.fragments.user}
` as TypedDocumentNode<
  NBASo5LeaderboardContenderDialogQuery,
  NBASo5LeaderboardContenderDialogQueryVariables
>;

const Content = styled(Vertical)`
  height: 100%;
  justify-content: space-between;
  position: relative;
`;
const Header = styled(SBHorizontal)`
  padding: var(--double-unit);
  overflow: hidden;
  > ${Vertical} {
    overflow: hidden;
  }
`;
const LineupWrapper = styled.div`
  padding: var(--double-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  so5LeaderboardContenderSlug: string;
};

export const So5LeaderboardContenderDialog = ({
  open,
  onClose,
  so5LeaderboardContenderSlug,
}: Props) => {
  const { data, loading } = useQuery(SO5_LEADERBOARD_CONTENDER_DIALOG_QUERY, {
    variables: {
      slug: so5LeaderboardContenderSlug,
    },
  });

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  const { so5LeaderboardContender } = data?.so5 || {};
  const { so5Lineup } = so5LeaderboardContender || {};

  if (!so5LeaderboardContender || !so5Lineup) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth="xs" onClose={onClose} hideHeader>
      {({ CloseButton }) => (
        <Content>
          <Header>
            <MyClubLink user={so5Lineup.user}>
              <Horizontal gap={1.5}>
                <Avatar user={so5Lineup.user} rounded size={2} />
                <Vertical gap={0}>
                  <BodyS bold>
                    <Ellipsis>
                      {so5Lineup.user.nbaUserProfile?.clubName}
                    </Ellipsis>
                  </BodyS>
                  <LabelS color="var(--c-nd-600)">
                    <Ellipsis>
                      <UserName user={so5Lineup.user} />
                    </Ellipsis>
                  </LabelS>
                </Vertical>
              </Horizontal>
            </MyClubLink>
            <CloseButton onClose={onClose} />
          </Header>
          <LineupWrapper>
            <So5Lineup so5LeaderboardContender={so5LeaderboardContender} />
          </LineupWrapper>
          <Vertical gap={0}>
            <LineupStatus so5LeaderboardContender={so5LeaderboardContender}>
              <LineupRanking
                so5LeaderboardContender={so5LeaderboardContender}
              />
            </LineupStatus>
            <LineupFooter so5LeaderboardContender={so5LeaderboardContender} />
          </Vertical>
        </Content>
      )}
    </Dialog>
  );
};
